// frontend/src/application/services/OfflineSyncService.ts
//
// Silnik Synchronizacji — warstwa aplikacji (logika biznesowa trybu offline).
// Odtwarza na serwerze operacje zakolejkowane offline, w kolejności FIFO,
// z zachowaniem spójności kolejki przy braku sieci.
//
// Zależności wyłącznie przez interfejsy (Dependency Inversion):
//  - ISyncQueueRepository — źródło zadań + potwierdzanie ich przetworzenia,
//  - ISyncApiGateway      — transport do backendu.

import type { ISyncApiGateway } from '../../domain/gateways/ISyncApiGateway'
import type { ISyncQueueRepository } from '../../infrastructure/repositories/SyncQueueRepository'
import { checkpoint, logError } from '../../infrastructure/debug/AppDebugLog'

export class OfflineSyncService {
  constructor(
    private readonly queue: ISyncQueueRepository,
    private readonly apiGateway: ISyncApiGateway,
  ) { }

  /**
   * Przetwarza całą oczekującą kolejkę synchronizacji.
   *
   * Algorytm:
   *  1. Pobiera oczekujące zadania (`getPending`) — repozytorium zwraca je już
   *     posortowane FIFO (najstarsze najpierw).
   *  2. Iteruje sekwencyjnie (`for...of`) — kolejność operacji ma znaczenie
   *     (np. `create` musi wyprzedzić `update` tej samej encji).
   *  3. Dla każdego zadania woła `apiGateway.push(entityType, action, payload)`:
   *     - sukces → `queue.dequeue(id)` (zadanie zdjęte z kolejki),
   *     - błąd   → loguje i **przerywa** pętlę (`break`); pozostałe zadania
   *       zostają nienaruszone, żeby nie zgubić kolejności — ponowna próba
   *       nastąpi przy następnym powrocie sieci.
   *
   * Metoda celowo NIE rzuca wyjątkiem: brak sieci jest normalnym, oczekiwanym
   * stanem pracy offline, a nie sytuacją wyjątkową.
   */
  async processQueue(): Promise<void> {
    const pending = await this.queue.getPending()
    checkpoint('SYNC', 'processQueue start', { count: pending.length })

    for (const task of pending) {
      checkpoint('SYNC', 'sync task', { id: task.id, entityType: task.entityType, action: task.action })
      try {
        await this.apiGateway.push(task.entityType, task.action, task.payload)
      } catch (error) {
        logError('SYNC', `sync task #${task.id} failed`, error)
        break
      }

      // Sukces — zdejmujemy zadanie z kolejki.
      // `id` wpisu odczytanego z repozytorium jest zawsze zdefiniowane.
      await this.queue.dequeue(task.id as number)
      checkpoint('SYNC', 'sync task completed', { id: task.id })
    }
    checkpoint('SYNC', 'processQueue end', { remaining: (await this.queue.getPending()).length })
  }
}
